import { getBlogPost, getRelatedBlogPosts } from "@/components/blog/api";
import { apolloClientToProps, initializeApollo } from "../../../lib/apollo";
import getTranslations from "../../../lib/getTranslations";
import searchProductsQuery from "../../../src/Apollo/query/searchProducts.graphql";
import Blog from "../../../src/components/blog/Blog";
import { BlogPostEntity } from "@/common/contracts/strapi.contracts";
import { HeaderLanguageSwitcherProvider } from "@/components/Header/HeaderLanguageSwitcherContext";
import { defaultLocale } from "@/i18n/config";
import MainLayout from "@/components/Layouts/MainLayout";

type BlogPageProps = {
  blogPost: BlogPostEntity | null;
  relatedBlogPosts: BlogPostEntity[] | null;
};

const BlogPage = ({ blogPost, relatedBlogPosts }: BlogPageProps) => {
  return <Blog blogPost={blogPost} relatedBlogPosts={relatedBlogPosts} />;
};

BlogPage.getLayout = (page: any, props: any) => {
  return (
    <HeaderLanguageSwitcherProvider
      value={{ urls: props.languageSwitcherUrls }}
    >
      <MainLayout>{page}</MainLayout>
    </HeaderLanguageSwitcherProvider>
  );
};

const blogUrlWithPrefix = (slug: string, locale: string) => {
  return locale === defaultLocale ? `/blog/${slug}` : `/${locale}/blog/${slug}`;
};

const createLanguageSwitcherUrls = (
  responseBlogPost: Awaited<ReturnType<typeof getBlogPost>>
) => {
  const attributes = responseBlogPost.blogPost?.attributes;
  if (!attributes) {
    return {};
  }
  const localizations = attributes.localizations?.data ?? [];
  const languageSwitcherUrls = localizations.reduce<Record<string, string>>(
    (urls, localization) => {
      const { locale, slug } = localization.attributes ?? {};
      if (locale && slug) {
        urls[locale] = blogUrlWithPrefix(slug, locale);
      }
      return urls;
    },
    {}
  );
  if (attributes.locale && attributes.slug) {
    languageSwitcherUrls[attributes.locale] = blogUrlWithPrefix(
      attributes.slug,
      attributes.locale
    );
  }
  return languageSwitcherUrls;
};

export const getServerSideProps = async ({
  locale,
  params,
}: {
  locale: string;
  params: { slug: string };
}) => {
  const { slug } = params as { slug: string };

  if (typeof slug !== "string") {
    return { notFound: true };
  }

  const [responseBlogPost, responseRelatedBlogPosts] = await Promise.all([
    getBlogPost({ locale, slug }),
    getRelatedBlogPosts({ locale, slug }),
  ]);

  if (!responseBlogPost.blogPost?.attributes) {
    return { notFound: true };
  }

  if (responseBlogPost.blogPost.attributes.slug !== slug) {
    const blogPostSlug = responseBlogPost.blogPost.attributes.slug ?? "";
    return {
      redirect: {
        destination: blogUrlWithPrefix(blogPostSlug, locale),
        permanent: true,
      },
    };
  }

  const client = initializeApollo();

  await client.query({
    query: searchProductsQuery,
    variables: {
      input: {
        query: "",
        ...responseBlogPost?.blogPost?.attributes?.search,
        pagination: {
          limit: 4,
        },
      },
      lang: locale?.toUpperCase(),
    },
  });

  return {
    props: {
      ...(await getTranslations(locale, ["blog"])),
      ...apolloClientToProps(client).props,
      blogPost: responseBlogPost.blogPost,
      relatedBlogPosts: responseRelatedBlogPosts.blogPosts,
      languageSwitcherUrls: createLanguageSwitcherUrls(responseBlogPost),
    },
  };
};

export default BlogPage;
